import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MD5 from 'crypto-js/md5';
import logoG4 from '../../assets/img/theme/iconG4.png';
import { useDispatch, useSelector } from 'react-redux';
import { setAgentProfilePicture } from 'store/reduce/agent-profile-picture';
import { AGENT_PROFILE_PICTURE_SLICE_NAME } from 'store/reduce/agent-profile-picture';

/**
 * Lógica de hook isolada para construir a URL do agente.
 */
const useAgentImageUrl = (login) => {
  const getBaseUrl = useCallback(() => {
    if (window.location.hostname === 'localhost')
      return process.env.REACT_APP_PROXY_URL;

    return window.location.origin;
  }, []);

  const constructUrl = useCallback(() => {
    if (!login) return null;

    const baseUrl = getBaseUrl();
    const hashedLogin = MD5(login);

    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

    return `${cleanBaseUrl}/configuration/src/public/img/${hashedLogin}.png`;
  }, [login, getBaseUrl]);

  return { constructUrl };
};

/**
 * Componente para exibir fotos de perfil de agentes com base no login,
 * com cache em Redux para a URL resolvida (sucesso ou fallback).
 */
export const MemoizedAgentProfilePicture = React.memo(
  ({ login, alt, className, style }) => {
    const dispatch = useDispatch();

    const cachedUrl = useSelector(
      (state) => state[AGENT_PROFILE_PICTURE_SLICE_NAME]?.imageMap[login]
    );

    const { constructUrl } = useAgentImageUrl(login);

    const [imageSrc, setImageSrc] = useState(() => cachedUrl || logoG4);

    useEffect(() => {
      if (cachedUrl) {
        setImageSrc(cachedUrl);
        return;
      }

      if (!login) {
        setImageSrc(logoG4);
        return;
      }

      const potentialUrl = constructUrl();

      const img = new Image();
      img.src = potentialUrl;

      img.onload = () => {
        dispatch(setAgentProfilePicture({ login, url: potentialUrl }));
        setImageSrc(potentialUrl);
      };

      img.onerror = () => {
        dispatch(setAgentProfilePicture({ login, url: logoG4 }));
        setImageSrc(logoG4);
      };
    }, [login, cachedUrl, constructUrl, dispatch]);

    return (
      <img
        src={imageSrc}
        alt={alt || `Perfil do agente ${login}`}
        className={className}
        style={style}
      />
    );
  }
);

MemoizedAgentProfilePicture.propTypes = {
  login: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
};

MemoizedAgentProfilePicture.displayName = 'MemoizedAgentProfilePicture';
